// tyrant-throne.midokuni.com
import IconButton from "Components/IconButton";
import { STAR } from "Constants/Common";
import domToImage from 'dom-to-image';
import { useQueryState } from "Hooks/useQueryState";
import { useStorageState } from "Hooks/useStorageState";
import IStyle from "Interfaces/IStyle";
import { useToast } from "Layouts/Toast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Image as ReactImage, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { copyToClipboard, getJson } from "Utils/Common";
import { IStellaSoraCharacter, StellaSoraCharacter, StellaSoraPotential } from "Views/StellaSora/Models";
import PotentialCard from "Views/StellaSora/PotentialCard";

const styles:IStyle = {
	cardContainer: {
		display: "flex", 
		flexDirection: "row", 
		flexWrap: "wrap"
	},
	input: {
		marginRight: "2em"
	},
}

interface IPotentialProps {
	pot: StellaSoraPotential;
	showTooltip?: boolean;
}
const DEFAULT_TEAM = [103, 111, 112];
interface IPotentialData {
	level: number;
	isCore?: boolean;
	isPlus?: boolean;
	raw?:StellaSoraPotential;
}
const potentialDataDecoder = (str:string):IPotentialData=>{
	if (str.length === 0) return {level:0, isCore:false};
	const isCore = str.startsWith("c");
	let levelStr = isCore ? str.substring(1) : str;
	const isPlus = levelStr.endsWith("p");
	levelStr = isPlus ? levelStr.substring(0, levelStr.length-1) : levelStr;
	const level = parseInt(levelStr);
	return {level, isCore, isPlus};
}
const potentialDataEncoder = (obj:IPotentialData)=>obj.level === 0 ? null : `${obj?.isCore ? "c" : ""}${obj.level}${obj?.isPlus ? "p" : ""}`
// Max 3 per team so total of 16*3 pots, 
// can allocate 2 characters per pot level 
// and still have leeway for url sharing
function EditablePotential({pot, showTooltip}:IPotentialProps) {
	const charaId = Math.floor(pot.id / 100)%1000;
	const [sp] = useSearchParams()
	const team = sp.get("team")?.split(",").map(s=>parseInt(s)) ?? DEFAULT_TEAM;
	const slot = team.indexOf(charaId);
	// id % 100 = unique per person's pot = Pot ID
	// const [level, setLevel] = useQueryState<number>(0, `${slot}${String(pot.id % 100).padStart(2,"0")}`);
	const [data, setData] = useQueryState<IPotentialData>({
		level: 0,
		isCore: false,
		isPlus: false,
	}, `${slot}${String(pot.id % 100).padStart(2,"0")}`, potentialDataDecoder, potentialDataEncoder);
	const {level, isCore, isPlus} = data
	// L = level, # or M
	// SID=L
	const border = "solid 5px ";
	const containerStyle:React.CSSProperties = {
		width: 140,
		border: border+(isCore ? "pink" : "rgba(0,0,0,0)"),
		borderRadius: 10,
		marginBottom: 10,
	}
	const cardStyle:React.CSSProperties = {
		opacity: level > 0 ? 1 : 0.5,
		fontWeight: isCore ? "bold" : "unset",
	}
	const buttonStyle:React.CSSProperties = {
		opacity: 1,
		margin: 0,
		lineHeight: 1,
		marginRight: 3,
		paddingLeft: 5,
		paddingRight: 5,
		paddingTop: 2,
		paddingBottom: 2,
		objectFit: "cover"
	}
	const inputStyle:React.CSSProperties = {
		border: "none",
		width: "25px",
		padding: 0,
		margin: 0,
		boxSizing: "border-box"
	}
	const maxLevel = Math.max(1,pot.maxLevel-3);
	const setLevel = (newLevel:number) => {
		newLevel %= maxLevel+1;
		if (newLevel <= 0) {
			setData({
				level: 0,
				isCore: false,
				isPlus: false,
			})
		} else {
			setData({
				...data,
				level: newLevel,
			});
		}
	}
	return (<span style={containerStyle} className={`editable-potential noselect ${level === 0 ? "disabled" : ""}`} id={pot.id.toString()}>
		<span style={cardStyle} tabIndex={0} 
			onClick={(e)=>{
				if (e.ctrlKey) {
					setData({...data, isPlus: !isPlus, level: Math.max(data.level, 1)})
				} else {
					setLevel(level+1);
				}
			}}
			onDoubleClick={(e)=>{
				if(!e.ctrlKey) setData({...data, level: maxLevel, isCore: true})
			}}
		>
			<PotentialCard plus={isPlus} pot={pot} level={level} key={pot.id} disableTooltip={!showTooltip} trigger={["hover"]} />
		</span>
		<div className="controls">
			<Button variant="outline-info" style={buttonStyle} onClick={(e)=>{setData({...data, isCore: !isCore})}}>{STAR}</Button>
			<input type="number" value={level} onChange={(e)=>{setLevel(parseInt(e.target.value))}} min={0} step={1} max={maxLevel} style={inputStyle}/>
			<Button variant="outline-info" style={buttonStyle} onClick={(e)=>setData({...data, isPlus:!isPlus})}>+</Button>
			<Button variant="outline-info" style={buttonStyle} onClick={(e)=>setLevel(maxLevel)}>M</Button>
			<Button variant="outline-danger" style={buttonStyle} onClick={(e)=>setLevel(0)}><span className="btn-close" style={{fontSize: "0.9em"}}/></Button>
		</div>
	</span>)
}

export default function Record() {
	const [saves, setSaves] = useStorageState<{[key: string]: string}>({}, "midokuni.stella-sora.record.saves")
	const [showTooltip, setShowTooltip] = useStorageState(false, "midokuni.stella-sora.record.tooltip")
	const [title, setTitle] = useQueryState("Tyrant Throne", "title");
	const { addToast } = useToast()
	const DATA_URL = 'https://raw.githubusercontent.com/AutumnVN/StellaSoraData/refs/heads/main/character.json';
	const [characters, setCharacters] = useState<StellaSoraCharacter[]>([]);
	const [pretty, setPretty] = useQueryState<boolean>(false, "pretty");
	const [teamIds, setTeamIds] = useQueryState(DEFAULT_TEAM, "team", 
		(v)=>v.split(",").map((s:string)=>parseInt(s)), 
		(ids:number[])=>ids.join(","));
	// const [teamIds, setTeamIds] = useState<[number,number,number]>([103, 111, 112]);
	const team = useMemo(() => {
		if (!!!characters || characters.length === 0) return [];
		const t = teamIds.map(i=>characters.find(c=>c.id===i));
		if (t.includes(undefined) || t.includes(null)) return [];
		return t;
	}, [teamIds, characters]);
	useEffect(() => {
		async function fetchData() {
			const charaTask = getJson(DATA_URL);
			const charas = Object.values(await charaTask).map(chara=>new StellaSoraCharacter(chara as IStellaSoraCharacter));
			charas.sort((a,b)=>a.name.localeCompare(b.name));
			setCharacters(charas);
		}
		fetchData();
	},[]);
	const rosterDiv = useRef(null);
	const [searchParams] = useSearchParams();
	const potCounts = team.map((chara,i) => {
		return chara.getPotentials(i===0).map(pots => pots.map(pot => {
			const decoded = potentialDataDecoder(searchParams.get(`${i}${String(pot.id % 100).padStart(2,"0")}`)??"")
			if (pot.rarity === "core") return Math.min(decoded.level, 1);
			return decoded.level;
		}).reduce((prev,curr)=>prev+curr,0)).reduce((prev,curr)=>prev+curr,0)
	})
	const prettyPots = team.map((chara,i) => {
		return chara.getAtomicPotentials(i===0).map(pot => {
			const decoded = potentialDataDecoder(searchParams.get(`${i}${String(pot.id % 100).padStart(2,"0")}`)??"")
			decoded.raw = pot;
			return decoded;
		}).filter(pot=>pot.level > 0)
	})
	prettyPots.forEach((cpot) =>cpot.sort((a,b)=>{
		const typeACore = a.raw.rarity === "core";
		const typeBCore = b.raw.rarity === "core";
		if(typeACore && !typeBCore) return -1;
		if(!typeACore && typeBCore) return 1;
		if (a.isCore && !b.isCore) return -1;
		if (!a.isCore && b.isCore) return 1;
		// if (a.isCore == b.isCore)
		let comp;
		comp = b.level - a.level;
		if (comp !== 0) return comp;
		if (a.isPlus && !b.isPlus) return -1;
		if (!a.isPlus && b.isPlus) return 1;
		comp = b.raw.rarityVal - a.raw.rarityVal;
		if (comp !== 0) return comp;
		return -a.raw.compare(b.raw);
		// return 0;
		// return a.level - b.level;
	}))
	
		async function downloadImage() {
			// setIsPic(true);
			try {
				const su = rosterDiv.current;
				const dataUrl = await domToImage.toPng(su);
				const link = document.createElement('a');
				link.href = dataUrl;
				link.download = 'roster.png';
				link.click();
			} catch (error) {
				addToast('Error capturing image:'+error);
				console.log(error)
				console.error('Error capturing or copying image:', error);
			}
			// setIsPic(false);
		}
		async function copyImage() {
			// setIsPic(true);
			try {
				const su = rosterDiv.current;
				const dataUrl = await domToImage.toPng(su); // error???
				const canvas:any = document.createElement('canvas');
				canvas.width = su.offsetWidth;
				canvas.height = su.offsetHeight;
				const ctx = canvas.getContext('2d');
				const img = new Image();
				img.src = dataUrl;
				img.onload = () => {
					ctx?.drawImage(img, 0, 0);
					canvas.toBlob(function(blob) { 
						const item = new ClipboardItem({ "image/png": blob });
						navigator.clipboard.write([item]); 
						addToast("Copied to clipboard");
					});
				};
			} catch (error) {
				addToast('Error capturing image:'+error);
				console.error('Error capturing or copying image:', error);
			}
			// setIsPic(false);
		}
	return (<>
	<h3 className="text-pink">Tyrant Throne - Potentials Builder</h3>

	{/* CORS issue Fix via proxy
	<IconButton 
		image="/logo512.png"
		label="📷 Screenshot"
		onClick={async () => copyImage()}
	/>
	<IconButton 
		image="/logo512.png"
		label="📷 Download"
		onClick={async () => downloadImage()}
	/> */}
	<IconButton 
		image="/logo512.png"
		label="Share URL"
		onClick={async () => {
			const shareText = window.location.href;
			copyToClipboard(shareText);
			addToast("URL has put in your clipboard!")
		}}
	/>
	<div>
		<Alert dismissible>
			Tips: 
			<ul>
				<li>Click to increase level</li>
				<li>Double-Click to set as CORE and MAX level</li>
				<li>Hold <span className="text-pink">CTRL</span> while clicking to toggle PLUS mark</li>
			</ul>
		</Alert>
	</div>
	<label>Title</label>
	<input id="title-input" type="text" style={styles.input} maxLength={30} value={title} onChange={(e)=>setTitle(e.target.value)}></input>

	<label>Load Saved State</label>
	<select style={styles.input}  id="save-select" value={Object.keys(saves).includes(title) ? title : ""} onChange={(e)=>{
		window.location.search = saves[e.target.value];
	}}>
		<option value="" disabled>Select State to Load</option>
		{Object.keys(saves).map((key)=><option id={`save_${key}`} key={key} value={key}>{key}</option>)}
	</select>
	<Button variant="violet" style={styles.selectButton} onClick={()=>{
		const newSaves = {
			...saves,
			[title]: window.location.search
		};
		setSaves(newSaves);
		addToast(`Saved current state as "${title}"`);
	}}>Save State</Button>
	<Button variant="danger" style={styles.selectButton} disabled={!Object.keys(saves).includes(title)} onClick={()=>{
		const newSaves = {
			...saves,
		};
		delete newSaves[title];
		setSaves(newSaves);
		addToast(`Deleted state "${title}"`);
	}}>Delete State</Button>
	<Button variant="danger" style={styles.selectButton} onClick={()=>{window.location.search=""}}>Clear Screen</Button>
	<Form.Check 
	type="switch"
	id="pretty-switch"
	checked={pretty}
	label="Prettify for screenshot?"
	onChange={(a)=>{
		setPretty(a.target.checked);
	}}
	/>
	{!pretty && <Form.Check 
	type="switch"
	id="show-tooltip"
	checked={showTooltip}
	label="Show tooltips?"
	onChange={(a)=>{
		setShowTooltip(a.target.checked);
	}}
	/>}
	<h2 className="text-pink">
		Total Pots: {potCounts.reduce((p,c)=>p+c,0)}
	</h2>
	{!pretty ? <div id="team-build" ref={rosterDiv} style={styles.cardContainer}>
		{team.map((chara, i)=>(<div key={`team_${i}_${chara.id}`}>
			<div style={{display: "inline-block",margin: 10}}>
			<input className="controls" list="stella-sora-characters" placeholder={chara.name} onChange={(e)=>{
				const id = characters.find(c=>c.name.toLocaleLowerCase() === e.target.value.toLocaleLowerCase())?.id;
				if(!!id && !isNaN(id)) {
					const list = teamIds;
					list[i] = id;
					setTeamIds([...list]);
				}
			}} />
			<h1 className="text-pink"><ReactImage src={chara.getIcon()} style={{width: 100}} />
				{chara.name} {i===0 ? "Main":"Support"} ({potCounts[i]})
			</h1>
			{chara.getPotentials(i === 0).map((pots, j) => {
				return (<div key={`${chara.id}_pots_${j}`} style={styles.cardContainer}>
					{pots.map(pot => <EditablePotential pot={pot} key={pot.id} showTooltip={showTooltip} />)}
				</div>)
			})}
			</div>
		</div>))}
	</div>
	: <div id="pretty-team-build" style={styles.cardContainer}>
	{prettyPots.map((pots, i)=>{
		const cores = pots.filter(pot=>pot.raw.rarityVal === 3)
		const req = pots.filter(pot=>pot.raw.rarityVal !== 3 && pot.isCore)
		const etc = pots.filter(pot=>pot.raw.rarityVal !== 3 && !pot.isCore)
		return (<div key={i}>
			<div style={{display: "inline-block",margin: 10}}>
				<h1 className="text-pink">
					<ReactImage src={team[i].getIcon()} style={{width: 100}} />
					{team[i].name} {i===0 ? "Main":"Support"} ({potCounts[i]})
				</h1>
				<div>
					<div hidden={cores.length===0}>Core</div>
					<div style={styles.cardContainer}>{cores.map(pot => <PotentialCard trigger={["hover"]} plus={pot.isPlus} pot={pot.raw} level={pot.level} key={pot.raw.id} />)}</div>
					<div hidden={req.length===0}>Required</div>
					<div style={styles.cardContainer}>{req.map(pot => <PotentialCard trigger={["hover"]} plus={pot.isPlus} pot={pot.raw} level={pot.level} key={pot.raw.id} />)}</div>
					<div hidden={etc.length===0}>Others</div>
					<div style={styles.cardContainer}>{etc.map(pot => <PotentialCard trigger={["hover"]} plus={pot.isPlus} pot={pot.raw} level={pot.level} key={pot.raw.id} />)}</div>
				</div>
			</div>
		</div>)})}
	</div>
	}
	<Row className="responsive-font"><p>The data used here is processed from <a href="https://stelladb.pages.dev">StellaDB</a></p></Row>
	<Row className="responsive-font"><p>I'm lazy to set up another webpage for SS. For now I'm putting it here.</p></Row>
	<datalist id="stella-sora-characters">
	{characters.map((chara, key) => (
			<option key={chara.id} value={chara.name}>{chara.name}</option>
		))
	}
</datalist>
	</>);
}